/**
 * Sector turns — WeGo on the sector clock (DIRECTION §9.2).
 *
 * A turn is: every faction's orders go in, the clock advances a fixed number
 * of days, every battle that falls inside the turn is resolved and applied,
 * and the clock reaches the turn's end. The continuous clock in sector.ts is
 * the substrate; a turn is a policy over it, so real time later is the same
 * call with a small step on a timer.
 *
 * Two ways to drive it:
 *  - `resolveSectorTurn(state, orders, resolve)` — one call, an injected
 *    resolver decides every battle (the harness; auto-resolve everywhere).
 *  - the **stepper** (`beginTurn` → `continueTurn` …) — the turn pauses at
 *    each battle with its `BattleSetup` so the host can choose per battle:
 *    auto-resolve, watch, or take command (BUILDLIST 56.4). The host applies
 *    the outcome and continues. Battles are met one at a time in clock order
 *    because each outcome can change who is where for the next.
 */

import { advanceSector, applyBattleOutcome, enterRunningBattle, issueOrder, leaveBattle as leaveBattleInSector, startModelBattle } from './sector';
import type { GameState } from '../types';
import type { BattleOutcome, BattleRecord, BattleSetup, Order, SectorState, SquadId } from './types';

export type SectorOrders = Record<SquadId, Order>;

export type BattleResolver = (setup: BattleSetup) => Promise<BattleOutcome> | BattleOutcome;

export interface TurnResult {
  state: SectorState;
  /** Battles fought during this turn, in order. */
  battles: BattleRecord[];
}

export const DEFAULT_TURN_DAYS = 1;

/** Apply a set of orders (squads not named keep theirs). */
export function applyOrders(state: SectorState, orders: SectorOrders): SectorState {
  let s = state;
  for (const [squadId, order] of Object.entries(orders)) {
    if (!s.squads.some(q => q.id === squadId)) continue;
    // an order the state refuses (securing a contested system, scouting a non-neighbour) is dropped, not fatal
    try { s = issueOrder(s, squadId, order); } catch { /* refused */ }
  }
  return s;
}

// -----------------------------------------------------------------------------
// The stepper
// -----------------------------------------------------------------------------

/** Where a turn in progress stands. `pending` is the battle the host must resolve before the clock moves again. */
export interface TurnStep {
  state: SectorState;
  /** The day this turn ends. */
  end: number;
  /** Battles fought so far this turn. */
  battles: BattleRecord[];
  /** A battle waiting for the host's decision (auto / watch / take command), or null. */
  pending: BattleSetup | null;
  /** The turn has reached its end (or the sector is decided) and the turn counter has advanced. */
  done: boolean;
  /** False for a step that only plays a running battle the player entered between turns: no time passes and no turn is counted. */
  countsTurn?: boolean;
}

/**
 * Between turns, enter a battle under way: a step whose pending battle is that one. Continue it with
 * the played outcome as usual; no time passes and no turn is counted (the clock waits, as it does for
 * any pending battle).
 */
export function enterBattle(state: SectorState, battleId: string): TurnStep {
  const s = enterRunningBattle(state, battleId);
  return { state: s, end: s.day, battles: [], pending: s.pending, done: false, countsTurn: false };
}

/** Orders in; the clock runs until the turn's end or the first battle. */
export function beginTurn(state: SectorState, orders: SectorOrders, turnDays = DEFAULT_TURN_DAYS): TurnStep {
  if (state.victor) return { state, end: state.day, battles: [], pending: null, done: true };
  const s = applyOrders(state, orders);
  return run({ state: s, end: s.day + turnDays, battles: [], pending: null, done: false }, s.battles.length);
}

/**
 * The host has resolved the pending battle: fold the outcome in and run on to
 * the next battle or the turn's end.
 */
export function continueTurn(step: TurnStep, outcome: BattleOutcome): TurnStep {
  if (!step.pending) throw new Error('continueTurn: no battle is pending');
  if (outcome.battleId !== step.pending.id) throw new Error(`continueTurn: outcome ${outcome.battleId} is not the pending battle ${step.pending.id}`);
  const before = step.state.battles.length - step.battles.length;
  const s = applyBattleOutcome(step.state, outcome);
  return run({ ...step, state: s, pending: null }, before);
}

/**
 * 62.17 step 4: the pending battle unfolds in the battle model instead of being resolved now
 * (auto-resolve with `rules.battleModel`); the turn runs on, and the battle lands when it ends.
 */
export function continueTurnWithModel(step: TurnStep): TurnStep {
  if (!step.pending) throw new Error('continueTurnWithModel: no battle is pending');
  const before = step.state.battles.length - step.battles.length;
  const s = startModelBattle(step.state);
  return run({ ...step, state: s, pending: null }, before);
}

/** The player leaves the pending battle part-way (111): it runs on in the model from where the match stood; the turn runs on. */
export function leaveBattleStep(step: TurnStep, finalState: GameState, seconds: number): TurnStep {
  if (!step.pending) throw new Error('leaveBattleStep: no battle is pending');
  const before = step.state.battles.length - step.battles.length;
  const s = leaveBattleInSector(step.state, finalState, seconds);
  return run({ ...step, state: s, pending: null }, before);
}

/** Advance from wherever the step stands to the next battle or the end of the turn. */
function run(step: TurnStep, battlesBefore: number): TurnStep {
  let s = step.state;
  let guard = 0;
  while (!s.victor && guard++ < 1000) {
    s = advanceSector(s, step.end - s.day);
    if (s.pending) return { ...step, state: s, battles: s.battles.slice(battlesBefore), pending: s.pending, done: false };
    if (s.day >= step.end - 1e-9) {
      // reached the end: check nothing else is due at this instant
      s = advanceSector(s, 0);
      if (s.pending) return { ...step, state: s, battles: s.battles.slice(battlesBefore), pending: s.pending, done: false };
      break;
    }
  }
  if (step.countsTurn !== false) s = { ...s, turn: s.turn + 1 };
  return { ...step, state: s, battles: s.battles.slice(battlesBefore), pending: null, done: true };
}

// -----------------------------------------------------------------------------
// One call, every battle auto-resolved
// -----------------------------------------------------------------------------

/**
 * Resolve one sector turn with an injected resolver deciding every battle.
 * Stops early only when the sector is decided.
 */
export async function resolveSectorTurn(
  state: SectorState,
  orders: SectorOrders,
  resolve: BattleResolver,
  turnDays = DEFAULT_TURN_DAYS,
): Promise<TurnResult> {
  let step = beginTurn(state, orders, turnDays);
  while (!step.done) {
    // with the battle model on, every battle unfolds in it; otherwise the resolver decides it now
    if (step.state.rules.battleModel) { step = continueTurnWithModel(step); continue; }
    const outcome = await resolve(step.pending!);
    step = continueTurn(step, outcome);
  }
  return { state: step.state, battles: step.battles };
}
